/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Styles for the chapter content tree.
 *
 * @file chapterContentTreeStyles.js
 * @author Gladson Samuel S
 * @since 2.0.0
 */

import { styled } from '@mui/material/styles';
import mixins from '../../../assets/styles/mixins.scss';
import CommonStyles from '../../../assets/styles/CommonStyles';
import { createScopedClasses, createScopedStyles } from '../../../assets/styles/createScopedStyles';

const borderDefault = '#dbd9e5';
const textSecondary = '#555555';
const textTertiary = '#767676';
const textDisabled = mixins.bulkEditCtaButtonDisabledColor;
const checkboxDisabledFill = mixins.bulkEditCtaButtonDisabledBGColor;
const surfaceDefault = '#fefefe';
const scrollStyles = CommonStyles().scrollContainer({
  scrollbarWidth: 4,
  scrollbarColor: '#e8e6f3 transparent',
  borderRadius: 8,
  border: `1px solid ${borderDefault}`
});

const chapterContentTreeStyles = {
  treeRoot: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    flex: 1,
    minHeight: 0
  },
  // Only ever rendered for variant 'schedule' (Quick Schedule) — safe to change without
  // touching the picker variant's tree, which never applies this class.
  instruction: {
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize18,
    fontWeight: mixins.fontWeight600,
    lineHeight: '28px',
    color: mixins.color_text_primary,
    margin: 0,

    [mixins['ml-max']]: {
      fontSize: 12,
      lineHeight: '20px'
    }
  },
  // Auto-name on: second line under the schedule instruction, explaining names get generated.
  autoNameInstruction: {
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize14,
    fontWeight: mixins.fontWeight400,
    lineHeight: '22px',
    color: mixins.zambezi,
    margin: 0,

    [mixins['ml-max']]: {
      fontSize: 12,
      lineHeight: '20px'
    }
  },
  scrollContainer: {
    display: 'flex',
    flexDirection: 'column',
    background: surfaceDefault,
    boxSizing: 'border-box',
    paddingBottom: 16,
    flex: 1,
    minHeight: 0,
    ...scrollStyles
  },
  node: {
    display: 'flex',
    flexDirection: 'column',
    borderBottom: `1px solid ${borderDefault}`,

    '&:last-child': {
      borderBottom: 'none'
    }
  },
  nestedNode: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    boxSizing: 'border-box'
  },
  row: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    boxSizing: 'border-box'
  },
  rowOuter: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    width: '100%',
    boxSizing: 'border-box'
  },
  rowContentMain: {
    display: 'flex',
    flex: 1,
    minWidth: 0
  },
  rowContent: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    width: '100%',
    boxSizing: 'border-box',
    minHeight: 48,
    paddingTop: 12,
    paddingBottom: 12,
    paddingRight: 8,
    paddingLeft: 'calc(8px + 40px * var(--depth, 0))'
  },
  rowContentModule: {
    minHeight: 24,
    paddingTop: 4,
    paddingBottom: 4,
    paddingRight: 8,
    paddingLeft: 'calc(8px + 40px * var(--depth, 0))'
  },
  rowContentDraggable: {
    cursor: 'grab',

    '&:hover, &:focus-within': {
      outline: `2px dotted ${mixins.amethyst}`,
      outlineOffset: -2
    }
  },
  dragHandle: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color: textTertiary,
    cursor: 'grab',
    '& svg': {
      fontSize: 16
    }
  },
  dragHandleDisabled: {
    color: textDisabled,
    cursor: 'default',
    opacity: 0.5,
    WebkitUserDrag: 'none',
    userSelect: 'none'
  },
  dragHandleWrapper: {
    display: 'inline-flex',
    alignItems: 'center',
    cursor: 'default'
  },
  expandButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    border: 'none',
    background: 'transparent',
    borderRadius: '50%',
    padding: 12,
    boxSizing: 'border-box',
    cursor: 'pointer',

    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)'
    },

    '& svg': {
      width: 8,
      height: 8,
      fontSize: 8
    }
  },
  checkboxWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    minWidth: 0,
    flex: 1
  },
  checkboxRoot: {
    padding: 0,
    marginRight: 4,
    color: mixins.amethyst,
    '&.Mui-checked': {
      color: mixins.amethyst
    },
    '&.MuiCheckbox-indeterminate': {
      color: mixins.amethyst
    },
    '& svg': {
      fontSize: 18
    },
    '&.Mui-disabled': {
      color: textDisabled,
      opacity: 1,
      '& $checkboxIcon': {
        color: textDisabled,
        '& rect': {
          fill: checkboxDisabledFill,
          stroke: textDisabled
        }
      }
    }
  },
  checkboxIcon: {},
  label: {
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: 16,
    lineHeight: '24px',
    color: mixins.color_text_primary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  labelTopLevel: {
    fontWeight: mixins.fontWeight500
  },
  labelModule: {
    fontWeight: mixins.fontWeight400,
    color: textSecondary,
    lineHeight: '20px',
    fontSize: 14
  },
  labelDisabled: {
    color: textDisabled
  },
  questionCount: {
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: 12,
    fontWeight: mixins.fontWeight500,
    lineHeight: '20px',
    color: textTertiary,
    marginLeft: 'auto',
    minWidth: 80,
    textAlign: 'right',
    flexShrink: 0
  },
  statusLabel: {
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: 12,
    fontWeight: mixins.fontWeight500,
    lineHeight: '20px',
    color: textTertiary,
    marginLeft: 'auto',
    flexShrink: 0
  },
  children: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    boxSizing: 'border-box'
  },
  contentPicker: {
    background: mixins.chalkWhiteFont,
    borderRadius: 16,
    padding: 24,
    boxSizing: 'border-box',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    maxHeight: 748,

    '& *': {
      boxSizing: 'border-box'
    }
  },
  contentPickerHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    minHeight: 40
  },
  contentPickerBackButton: {
    boxSizing: 'border-box',
    border: 'none',
    padding: 0,
    background: 'transparent',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',

    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)'
    },
    '& svg': {
      width: 44,
      height: 44
    }
  },
  contentPickerTitle: {
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: 20,
    fontWeight: mixins.fontWeight500,
    lineHeight: '28px',
    letterSpacing: '-0.4px',
    color: mixins.color_text_primary,
    margin: 0
  },
  contentPickerSections: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 4,
    alignItems: 'center'
  },
  contentPickerSectionsLabel: {
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: 12,
    fontWeight: mixins.fontWeight500,
    lineHeight: '20px',
    color: mixins.greyScaleColor,
    flexShrink: 0
  },
  contentPickerSectionChip: {
    backgroundColor: mixins.feedBackColor,
    borderRadius: 4,
    padding: '2px 4px',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: 12,
    fontWeight: mixins.fontWeight500,
    lineHeight: '20px',
    color: mixins.timeIconColor,
    flexShrink: 0
  },
  contentPickerSummary: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    alignItems: 'center'
  },
  contentPickerSummaryBadge: {
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: 12,
    fontWeight: mixins.fontWeight500,
    lineHeight: '18px',
    color: mixins.amethyst,
    backgroundColor: mixins.badgeBgColor,
    borderRadius: 4,
    padding: '0 6px 2px 6px'
  },
  contentPickerActions: {
    display: 'flex',
    gap: 12,
    alignItems: 'center',
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
    alignSelf: 'flex-start'
  }
};

export const classes = createScopedClasses('aet-chapter-content-tree', chapterContentTreeStyles);

export const scopedStyles = createScopedStyles(chapterContentTreeStyles, classes);

export const ChapterContentTreeStylesRoot = styled('div')(scopedStyles);

export default chapterContentTreeStyles;
